import { getSafeClient } from "@/integrations/supabase/safeClient";
import { Database } from "@/integrations/supabase/types";

// Define the interface based on the actual fields in the buyer_requests table
export interface BuyerRequest {
  id: string;
  niche: string;
  drMin: number;
  daMin: number;
  budgetMin: number;
  budgetMax: number;
  linkType: string;
  notes: string;
  expiresAt: string;
  createdAt: string;
  userId: string;
  userName?: string | null;
  userAvatar?: string | null;
}

/**
 * Fetches all buyer requests
 * @param page Current page number
 * @param limit Number of items per page
 * @param searchQuery Optional search query for filtering
 * @param category Optional category for filtering
 * @returns An object containing the requests array and total count
 */
export async function fetchBuyerRequests(
  page: number = 1,
  limit: number = 12,
  searchQuery: string = "",
  category: string = "all"
): Promise<{ requests: BuyerRequest[], count: number }> {
  try {
    const start = (page - 1) * limit;
    const end = page * limit - 1;

    const supabaseClient = await getSafeClient();
    
    // First fetch the buyer requests without joins
    let query = supabaseClient
      .from("buyer_requests")
      .select('*', { count: "exact" })
      .order("created_at", { ascending: false })
      .range(start, end);

    // Add category filter if not "all"
    if (category !== "all") {
      query = query.eq("niche", category);
    }

    // Add search query filter if provided
    if (searchQuery) {
      query = query.or(`niche.ilike.%${searchQuery}%, notes.ilike.%${searchQuery}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("Error fetching buyer requests:", error);
      throw new Error(error.message);
    }

    if (!data || data.length === 0) {
      return { requests: [], count: 0 };
    }

    // Transform the data to match without profile info first
    const requests: BuyerRequest[] = data.map(item => ({
      id: item.id,
      niche: item.niche,
      drMin: item.dr_min || 0,
      daMin: item.da_min || 0,
      budgetMin: item.budget_min_cents ? item.budget_min_cents / 100 : 0,
      budgetMax: item.budget_max_cents ? item.budget_max_cents / 100 : 0,
      linkType: item.link_type || '',
      notes: item.notes || '',
      expiresAt: item.expires_at,
      createdAt: item.created_at,
      userId: item.user_id,
      userName: "Loading...",
      userAvatar: null
    }));

    // Get unique user IDs to fetch profiles separately
    const userIds = [...new Set(data.map(item => item.user_id))];
    
    // Fetch profiles if there are user IDs
    if (userIds.length > 0) {
      try {
        const { data: profilesData, error: profilesError } = await supabaseClient
          .from('profiles')
          .select('id, name, avatar_url')
          .in('id', userIds);
          
        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
        } else if (profilesData) {
          // Create a lookup map for quick profile access
          const profilesMap = profilesData.reduce((acc, profile) => {
            acc[profile.id] = profile;
            return acc;
          }, {} as Record<string, any>);
          
          // Update requests with profile data
          requests.forEach(request => {
            const profile = profilesMap[request.userId];
            if (profile) {
              request.userName = profile.name || "Anonymous";
              request.userAvatar = profile.avatar_url;
            } else {
              request.userName = "Anonymous";
            }
          });
        }
      } catch (profileError) {
        console.error('Error processing profiles:', profileError);
      }
    }

    return { 
      requests, 
      count: count || 0 
    };
  } catch (error) {
    console.error("Error in fetchBuyerRequests:", error);
    throw error;
  }
}

/**
 * Fetches a single buyer request by ID
 * @param requestId The ID of the request to fetch
 * @returns The buyer request data
 */
export async function fetchBuyerRequestById(requestId: string): Promise<BuyerRequest | null> {
  try {
    // First, fetch the buyer request without joins
    const supabaseClient = await getSafeClient();
    const { data, error } = await supabaseClient
      .from("buyer_requests")
      .select('*')
      .eq("id", requestId)
      .single();

    if (error) {
      console.error("Error fetching buyer request by ID:", error);
      throw new Error(error.message);
    }

    if (!data) return null;

    // Create the request object without profile information
    const request: BuyerRequest = {
      id: data.id,
      niche: data.niche,
      drMin: data.dr_min || 0,
      daMin: data.da_min || 0,
      budgetMin: data.budget_min_cents ? data.budget_min_cents / 100 : 0,
      budgetMax: data.budget_max_cents ? data.budget_max_cents / 100 : 0,
      linkType: data.link_type || '',
      notes: data.notes || '',
      expiresAt: data.expires_at,
      createdAt: data.created_at,
      userId: data.user_id,
      userName: "Anonymous",
      userAvatar: null
    };

    // Fetch the profile separately
    if (data.user_id) {
      try {
        const { data: profileData, error: profileError } = await supabaseClient
          .from('profiles')
          .select('name, avatar_url')
          .eq('id', data.user_id)
          .single();
          
        if (!profileError && profileData) {
          request.userName = profileData.name || "Anonymous";
          request.userAvatar = profileData.avatar_url;
        }
      } catch (profileError) {
        console.error('Error fetching profile:', profileError);
      }
    }

    return request;
  } catch (error) {
    console.error("Error in fetchBuyerRequestById:", error);
    throw error;
  }
}

/**
 * Creates a new buyer request
 * @param requestData The request data to create
 * @returns The created request data
 */
export async function createBuyerRequest(
  requestData: {
    niche: string;
    link_type?: string;
    dr_min?: number;
    da_min?: number;
    budget_min_cents?: number;
    budget_max_cents?: number;
    notes?: string;
    expires_at: string;
    user_id: string;
  }
): Promise<BuyerRequest> {
  try {
    // Insert the buyer request
    const supabaseClient = await getSafeClient();
    const { data, error } = await supabaseClient
      .from("buyer_requests")
      .insert(requestData)
      .select('*')
      .single();

    if (error) {
      console.error("Error creating buyer request:", error);
      throw new Error(error.message);
    }

    // Create the request object without profile information
    const request: BuyerRequest = {
      id: data.id,
      niche: data.niche,
      drMin: data.dr_min || 0,
      daMin: data.da_min || 0,
      budgetMin: data.budget_min_cents ? data.budget_min_cents / 100 : 0,
      budgetMax: data.budget_max_cents ? data.budget_max_cents / 100 : 0,
      linkType: data.link_type || '',
      notes: data.notes || '',
      expiresAt: data.expires_at,
      createdAt: data.created_at,
      userId: data.user_id,
      userName: "Anonymous",
      userAvatar: null
    };

    // Fetch the profile separately
    if (data.user_id) {
      try {
        const { data: profileData, error: profileError } = await supabaseClient
          .from('profiles')
          .select('name, avatar_url')
          .eq('id', data.user_id)
          .single();
          
        if (!profileError && profileData) {
          request.userName = profileData.name || "Anonymous";
          request.userAvatar = profileData.avatar_url;
        }
      } catch (profileError) {
        console.error('Error fetching profile:', profileError);
      }
    }

    return request;
  } catch (error) {
    console.error("Error in createBuyerRequest:", error);
    throw error;
  }
}

/**
 * Fetches buyer requests created by a specific user
 * @param userId The ID of the user
 * @returns An array of buyer requests created by the user
 */
export async function fetchUserBuyerRequests(userId: string): Promise<BuyerRequest[]> {
  try {
    // First fetch the buyer requests without joins
    const supabaseClient = await getSafeClient();
    const { data, error } = await supabaseClient
      .from("buyer_requests")
      .select('*')
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching user buyer requests:", error);
      throw new Error(error.message);
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Transform the data without profile information
    const requests: BuyerRequest[] = data.map(item => ({
      id: item.id,
      niche: item.niche,
      drMin: item.dr_min || 0,
      daMin: item.da_min || 0,
      budgetMin: item.budget_min_cents ? item.budget_min_cents / 100 : 0,
      budgetMax: item.budget_max_cents ? item.budget_max_cents / 100 : 0,
      linkType: item.link_type || '',
      notes: item.notes || '',
      expiresAt: item.expires_at,
      createdAt: item.created_at,
      userId: item.user_id,
      userName: "Anonymous",
      userAvatar: null
    }));

    // Fetch the user profile separately
    try {
      const { data: profileData, error: profileError } = await supabaseClient
        .from('profiles')
        .select('name, avatar_url')
        .eq('id', userId)
        .single();
        
      if (!profileError && profileData) {
        // Apply the profile data to all requests since they're from the same user
        requests.forEach(request => {
          request.userName = profileData.name || "Anonymous";
          request.userAvatar = profileData.avatar_url;
        });
      }
    } catch (profileError) {
      console.error('Error fetching profile:', profileError);
    }

    return requests;
  } catch (error) {
    console.error("Error in fetchUserBuyerRequests:", error);
    throw error;
  }
}
